import { useState, useCallback } from 'react';
import { useLanguage } from '../../../i18n';
import { analyzePlanningFile, type AnalyzePlanningResponse, type ScreenshotAnalysis as ApiScreenshotAnalysis } from '../../../api';
import type { ScreenshotAnalysis } from '../types';
import { generateDisplayFileName } from '../types';

interface UsePlanningAnalyzerReturn {
  screenshotAnalyses: ScreenshotAnalysis[];
  extractedPlanningItems: string[];
  isAnalyzing: boolean;
  analyzePlanningFiles: (files: File[], featureName?: string) => Promise<void>;
  clearAnalysis: () => void;
}

// 파일 타입 판별
function getFileType(file: File): 'image' | 'pdf' | 'text' {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) return 'pdf';
  return 'text';
}

// File을 base64로 변환
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // data:image/png;base64, 부분 제거
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// API 응답을 프론트엔드 타입으로 변환
function convertApiResponse(
  apiAnalysis: ApiScreenshotAnalysis | null | undefined,
  fileName: string
): ScreenshotAnalysis | null {
  if (!apiAnalysis) return null;
  
  return {
    suggestedFeatureName: apiAnalysis.suggested_feature_name || fileName,
    uiComponents: apiAnalysis.ui_components || [],
    userFlow: apiAnalysis.user_flow || [],
    kpiSuggestions: apiAnalysis.kpi_suggestions || [],
  };
}

export function usePlanningAnalyzer(): UsePlanningAnalyzerReturn {
  const { lang } = useLanguage();
  const [screenshotAnalyses, setScreenshotAnalyses] = useState<ScreenshotAnalysis[]>([]);
  const [extractedPlanningItems, setExtractedPlanningItems] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const analyzePlanningFiles = useCallback(
    async (files: File[], featureName?: string) => {
      if (files.length === 0) return;

      setIsAnalyzing(true);
      setScreenshotAnalyses([]);
      setExtractedPlanningItems([]);

      const allKpiSuggestions: string[] = [];
      const allAnalyses: ScreenshotAnalysis[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileType = getFileType(file);
        
        // 변경된 파일명 생성 (확장자 제외)
        const displayFileName = featureName 
          ? generateDisplayFileName(featureName, 'planning', i, file.name).replace(/\.[^/.]+$/, '')
          : file.name.replace(/\.[^/.]+$/, '');

        try {
          // 파일을 base64로 변환
          const base64Content = await fileToBase64(file);
          
          // API 호출
          const response: AnalyzePlanningResponse = await analyzePlanningFile({
            file_content: base64Content,
            file_name: file.name,
            file_type: fileType,
            feature_name: featureName,
            lang: lang as 'ko' | 'en',
          });

          if (fileType === 'image' && response.screenshot_analysis) {
            // 이미지 분석 결과
            const analysis = convertApiResponse(response.screenshot_analysis, displayFileName);
            if (analysis) {
              // 파일명 prefix 추가
              analysis.uiComponents = analysis.uiComponents.map(item => 
                item.startsWith(`[${displayFileName}]`) ? item : `[${displayFileName}] ${item}`
              );
              analysis.userFlow = analysis.userFlow.map(item => 
                item.startsWith(`[${displayFileName}]`) ? item : `[${displayFileName}] ${item}`
              );
              
              allAnalyses.push(analysis);
              allKpiSuggestions.push(...analysis.kpiSuggestions);
            }
          } else if (response.extracted_items && response.extracted_items.length > 0) {
            // PDF/텍스트 파일 분석 결과
            const prefixedItems = response.extracted_items.map(item =>
              item.startsWith(`[${displayFileName}]`) ? item : `[${displayFileName}] ${item}`
            );
            allKpiSuggestions.push(...prefixedItems);
          }
        } catch (error) {
          console.error(`Failed to analyze file ${file.name}:`, error);
          
          // API 실패 시 폴백: 기본 메시지 추가
          const fallbackMessage = lang === 'ko' 
            ? `[${displayFileName}] 파일 분석 실패 - 수동으로 기획 의도를 입력해주세요`
            : `[${displayFileName}] File analysis failed - Please enter planning intent manually`;
          allKpiSuggestions.push(fallbackMessage);
        }
      }

      setScreenshotAnalyses(allAnalyses);
      setExtractedPlanningItems(allKpiSuggestions);
      setIsAnalyzing(false);
    },
    [lang]
  );

  const clearAnalysis = useCallback(() => {
    setScreenshotAnalyses([]);
    setExtractedPlanningItems([]);
  }, []);

  return {
    screenshotAnalyses,
    extractedPlanningItems,
    isAnalyzing,
    analyzePlanningFiles,
    clearAnalysis,
  };
}
